import React, { useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { EvalApiService } from '../../services/EvalApiService';

export interface CreateModelBody {
  model_name: string;
  model_type: 'OD' | 'VE' | 'OOS';
  weights_uri: string;
  config_uri?: string | null;
  architecture?: string | null;
  params?: Record<string, unknown> | null;
  description?: string | null;
}

interface CreateModelDialogProps {
  modelType: 'OD' | 'VE' | 'OOS';
  onCreate: (body: CreateModelBody) => Promise<void>;
  onCancel: () => void;
}

export const CreateModelDialog: React.FC<CreateModelDialogProps> = ({
  modelType,
  onCreate,
  onCancel,
}) => {
  const { token } = useAuthContext();
  const [modelName, setModelName] = useState('');
  const [weightsUri, setWeightsUri] = useState('');
  const [configUri, setConfigUri] = useState('');
  const [architecture, setArchitecture] = useState('');
  const [description, setDescription] = useState('');
  const [paramsText, setParamsText] = useState('{}');
  const [submitting, setSubmitting] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const configRequired = modelType === 'VE' || modelType === 'OOS';
  const canSubmit =
    modelName.trim() !== '' &&
    weightsUri.trim() !== '' &&
    (!configRequired || configUri.trim() !== '') &&
    (modelType !== 'OD' || architecture.trim() !== '');

  const submit = async () => {
    setError(null);
    let params: Record<string, unknown> = {};
    if (paramsText.trim()) {
      try {
        params = JSON.parse(paramsText);
      } catch {
        setError('Params must be valid JSON');
        return;
      }
    }
    setSubmitting(true);
    try {
      await onCreate({
        model_name: modelName.trim(),
        model_type: modelType,
        weights_uri: weightsUri.trim(),
        config_uri: configUri.trim() || null,
        architecture: architecture.trim() || null,
        params,
        description: description.trim() || null,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create model');
      setSubmitting(false);
    }
  };

  const prettify = () => {
    try {
      setParamsText(JSON.stringify(JSON.parse(paramsText || '{}'), null, 2));
      setError(null);
    } catch {
      setError('Params must be valid JSON to prettify');
    }
  };

  const copyFromLatest = async () => {
    setError(null);
    setCopying(true);
    try {
      const { items } = await EvalApiService.listModels(token ?? '', { model_type: modelType });
      const latest = [...items].sort((a, b) => b.registered_at.localeCompare(a.registered_at))[0];
      if (!latest) {
        setError(`No existing ${modelType} model to copy from`);
        return;
      }
      const full = await EvalApiService.getModel(token ?? '', latest.model_id);
      setParamsText(JSON.stringify(full?.params ?? {}, null, 2));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to copy params');
    } finally {
      setCopying(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 24,
          width: 540,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)' }}>
          Add {modelType} model
        </div>

        <div className="eval-form-group">
          <label className="eval-form-label">Version (model name)</label>
          <input
            className="eval-form-input"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            placeholder="e.g. 5.2_village"
          />
        </div>

        <div className="eval-form-group">
          <label className="eval-form-label">Weights URI</label>
          <input
            className="eval-form-input"
            value={weightsUri}
            onChange={(e) => setWeightsUri(e.target.value)}
            placeholder="gs://bucket/path/weights.ckpt"
          />
        </div>

        <div className="eval-form-group">
          <label className="eval-form-label">Config URI{configRequired ? ' (required)' : ' (optional)'}</label>
          <input
            className="eval-form-input"
            value={configUri}
            onChange={(e) => setConfigUri(e.target.value)}
            placeholder="gs://bucket/path/config.json"
          />
        </div>

        {modelType === 'OD' && (
          <div className="eval-form-group">
            <label className="eval-form-label">Architecture</label>
            <input
              className="eval-form-input"
              value={architecture}
              onChange={(e) => setArchitecture(e.target.value)}
              placeholder="e.g. v3_1b_swin_v2_dino"
            />
          </div>
        )}

        <div className="eval-form-group">
          <label className="eval-form-label">Description</label>
          <input
            className="eval-form-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="eval-form-group">
          <label className="eval-form-label">Params (JSON)</label>
          <textarea
            className="eval-form-textarea"
            value={paramsText}
            onChange={(e) => setParamsText(e.target.value)}
            rows={4}
            style={{ fontFamily: 'var(--font-mono)' }}
          />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
            <button
              type="button"
              className="eval-btn-ghost"
              style={{ fontSize: 11, padding: '2px 8px' }}
              onClick={copyFromLatest}
              disabled={copying}
            >
              {copying ? 'Copying…' : 'Copy from latest'}
            </button>
            <button
              type="button"
              className="eval-btn-ghost"
              style={{ fontSize: 11, padding: '2px 8px' }}
              onClick={prettify}
            >
              Prettify
            </button>
          </div>
        </div>

        {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="eval-btn-ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button className="eval-btn" onClick={submit} disabled={!canSubmit || submitting}>
            {submitting ? 'Creating…' : 'Create model'}
          </button>
        </div>
      </div>
    </div>
  );
};
